'use client';

import { useMemo, useState } from 'react';
import {
  buildCookieConsentSnippet,
  defaultCookieConsentOptions,
  type CookieConsentOptions,
} from '@/lib/browser-widgetgen-tools';

async function copyText(value: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = value;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.append(textarea);
  textarea.select();
  document.execCommand('copy');
  textarea.remove();
}

export default function BrowserCookieConsentGenerator() {
  const [options, setOptions] = useState<CookieConsentOptions>(defaultCookieConsentOptions);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  const snippet = useMemo(() => buildCookieConsentSnippet(options), [options]);

  function set<K extends keyof CookieConsentOptions>(key: K, value: CookieConsentOptions[K]) {
    setOptions((prev) => ({ ...prev, [key]: value }));
  }

  async function handleCopy() {
    try {
      await copyText(snippet);
      setCopyMessage('Snippet copied to clipboard.');
    } catch {
      setCopyMessage('The browser could not copy the snippet.');
    }
  }

  const posStyle: React.CSSProperties =
    options.position === 'top' ? { top: 0, left: 0, right: 0 } : { bottom: 0, left: 0, right: 0 };

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Client-side</span>
        <h2 className="tool-live-title">Cookie consent banner generator</h2>
        <p className="tool-live-subtitle">
          Configure a cookie-consent banner and copy a self-contained HTML, CSS, and JavaScript
          snippet. Built in your browser.
        </p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          <div className="tool-live-controls">
            <label className="tool-live-field tool-live-field-full">
              <span>Banner message</span>
              <textarea
                onChange={(e) => set('message', e.target.value)}
                rows={2}
                value={options.message}
              />
            </label>
            <label className="tool-live-field">
              <span>Accept button label</span>
              <input
                onChange={(e) => set('acceptLabel', e.target.value)}
                type="text"
                value={options.acceptLabel}
              />
            </label>
            <label className="tool-live-field">
              <span>Decline button label</span>
              <input
                onChange={(e) => set('declineLabel', e.target.value)}
                type="text"
                value={options.declineLabel}
              />
            </label>
            <label className="tool-live-field">
              <span>Position</span>
              <select
                onChange={(e) =>
                  set('position', e.target.value as CookieConsentOptions['position'])
                }
                value={options.position}
              >
                <option value="bottom">Bottom</option>
                <option value="top">Top</option>
              </select>
            </label>
            <label className="tool-live-field">
              <span>Show decline button</span>
              <select
                onChange={(e) => set('showDecline', e.target.value === 'yes')}
                value={options.showDecline ? 'yes' : 'no'}
              >
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
            <label className="tool-live-field">
              <span>Background color</span>
              <input
                onChange={(e) => set('backgroundColor', e.target.value)}
                type="color"
                value={options.backgroundColor}
              />
            </label>
            <label className="tool-live-field">
              <span>Text color</span>
              <input
                onChange={(e) => set('textColor', e.target.value)}
                type="color"
                value={options.textColor}
              />
            </label>
            <label className="tool-live-field">
              <span>Button color</span>
              <input
                onChange={(e) => set('buttonColor', e.target.value)}
                type="color"
                value={options.buttonColor}
              />
            </label>
            <label className="tool-live-field">
              <span>Button text color</span>
              <input
                onChange={(e) => set('buttonTextColor', e.target.value)}
                type="color"
                value={options.buttonTextColor}
              />
            </label>
            <label className="tool-live-field">
              <span>Link text</span>
              <input
                onChange={(e) => set('linkText', e.target.value)}
                type="text"
                value={options.linkText}
              />
            </label>
            <label className="tool-live-field">
              <span>Link URL</span>
              <input
                onChange={(e) => set('linkUrl', e.target.value)}
                type="text"
                value={options.linkUrl}
              />
            </label>
            <label className="tool-live-field">
              <span>localStorage key</span>
              <input
                onChange={(e) => set('storageKey', e.target.value)}
                type="text"
                value={options.storageKey}
              />
            </label>
          </div>
          <div className="tool-live-actions">
            <button className="button button-secondary" onClick={handleCopy} type="button">
              Copy snippet
            </button>
          </div>
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <section className="tool-live-preview">
          <div
            style={{
              position: 'relative',
              minHeight: 180,
              borderRadius: 12,
              border: '1px solid rgba(0,0,0,0.1)',
              overflow: 'hidden',
              background:
                'repeating-linear-gradient(45deg,#f3f4f6,#f3f4f6 10px,#e5e7eb 10px,#e5e7eb 20px)',
            }}
          >
            <div
              style={{
                position: 'absolute',
                ...posStyle,
                background: options.backgroundColor,
                color: options.textColor,
                padding: '16px 20px',
                fontSize: 14,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 12,
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <p style={{ margin: 0, flex: '1 1 220px', lineHeight: 1.5 }}>
                  {options.message}
                  {options.linkText.trim() ? (
                    <span
                      style={{
                        color: options.buttonColor,
                        textDecoration: 'underline',
                        marginLeft: 6,
                      }}
                    >
                      {options.linkText}
                    </span>
                  ) : null}
                </p>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  {options.showDecline ? (
                    <span
                      style={{
                        background: 'transparent',
                        color: options.textColor,
                        border: `1px solid ${options.textColor}`,
                        borderRadius: 6,
                        padding: '8px 16px',
                      }}
                    >
                      {options.declineLabel || 'Decline'}
                    </span>
                  ) : null}
                  <span
                    style={{
                      background: options.buttonColor,
                      color: options.buttonTextColor,
                      borderRadius: 6,
                      padding: '8px 16px',
                    }}
                  >
                    {options.acceptLabel || 'Accept'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <label className="tool-live-field tool-live-field-full">
            <span>HTML snippet</span>
            <textarea readOnly rows={12} value={snippet} />
          </label>
          <p className="tool-live-panel-note">
            The banner remembers the choice in localStorage. Generated locally in your browser.
          </p>
        </section>
      </div>
    </div>
  );
}
